import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Essay, GrammarError, FavoriteItem, PracticeItem, ErrorCategory, ReviewResult } from '../types';
import type { AppUser } from '../api/client';
import { ERROR_CATEGORY_LABELS } from '../types';
import {
  analyzeEssay,
  saveEssayToBackend,
  fetchUserEssays,
  deleteEssayFromBackend,
  updateEssayErrorInBackend,
  fetchFavoritesFromBackend,
  addFavoriteToBackend,
  removeFavoriteFromBackend,
  reviewFavoriteInBackend,
} from '../api/client';

const normalizeForCompare = (text: string) =>
  text.trim().toLowerCase().replace(/\s+/g, ' ');

const sentenceKey = (text: string) => normalizeForCompare(text).replace(/[^\w\s]/g, '');
const DEFAULT_ESSAY_TITLE = '未命名作文';
const REVIEW_STAGE_INTERVAL_DAYS = [0, 1, 3, 7, 14, 30, 45];
const REVIEW_RETRY_AFTER_WRONG_MS = 1000 * 60 * 60 * 6;
const REVIEW_RETRY_AFTER_REPEAT_WRONG_MS = 1000 * 60 * 60;

const parseBackendEssayId = (essay: Essay | null | undefined, essayId: string) => {
  if (essay?.dbId && Number.isFinite(essay.dbId)) return essay.dbId;
  if (essayId.startsWith('db-')) {
    const parsed = Number(essayId.slice(3));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseBackendErrorId = (errorId: string) => {
  const match = /^dberr-\d+-(\d+)$/.exec(errorId);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const addDays = (timestampMs: number, days: number) => timestampMs + days * 24 * 60 * 60 * 1000;

const getStageIntervalDays = (stage: number) => {
  if (stage <= 0) return 0;
  return REVIEW_STAGE_INTERVAL_DAYS[Math.min(stage, REVIEW_STAGE_INTERVAL_DAYS.length - 1)] || 0;
};

const getNextReviewAtFromStage = (stage: number, timestampMs: number) => {
  const intervalDays = getStageIntervalDays(stage);
  return intervalDays <= 0 ? timestampMs : addDays(timestampMs, intervalDays);
};

const shouldMasterFavorite = (reviewStage: number, consecutiveCorrect: number, wrongCount: number) => {
  if (wrongCount >= 2) {
    return reviewStage >= 5 && consecutiveCorrect >= 4;
  }
  return reviewStage >= 3 && consecutiveCorrect >= 3;
};

const buildInitialFavoriteState = (error: GrammarError, initialReviewResult?: ReviewResult | null): FavoriteItem => {
  const timestampMs = Date.now();
  if (initialReviewResult === 'correct') {
    return {
      error,
      addedAt: new Date(timestampMs).toISOString(),
      reviewCount: 1,
      lastReviewedAt: new Date(timestampMs).toISOString(),
      reviewStage: 1,
      nextReviewAt: new Date(getNextReviewAtFromStage(1, timestampMs)).toISOString(),
      lastReviewResult: 'correct',
      consecutiveCorrect: 1,
      wrongCount: 0,
      isMastered: false,
      masteredAt: null,
    };
  }

  return {
    error,
    addedAt: new Date(timestampMs).toISOString(),
    reviewCount: 0,
    lastReviewedAt: null,
    reviewStage: 0,
    nextReviewAt: new Date(timestampMs).toISOString(),
    lastReviewResult: null,
    consecutiveCorrect: 0,
    wrongCount: 0,
    isMastered: false,
    masteredAt: null,
  };
};

const applyLocalReviewResult = (favorite: FavoriteItem, result: ReviewResult): FavoriteItem => {
  const timestampMs = Date.now();

  if (result === 'correct') {
    const nextStage = Math.min(favorite.reviewStage + 1, REVIEW_STAGE_INTERVAL_DAYS.length - 1);
    const consecutiveCorrect = favorite.consecutiveCorrect + 1;
    const isMastered = shouldMasterFavorite(nextStage, consecutiveCorrect, favorite.wrongCount);
    return {
      ...favorite,
      reviewCount: favorite.reviewCount + 1,
      lastReviewedAt: new Date(timestampMs).toISOString(),
      reviewStage: nextStage,
      nextReviewAt: isMastered ? null : new Date(getNextReviewAtFromStage(nextStage, timestampMs)).toISOString(),
      lastReviewResult: 'correct',
      consecutiveCorrect,
      isMastered,
      masteredAt: isMastered ? new Date(timestampMs).toISOString() : null,
    };
  }

  const retryDelayMs =
    favorite.lastReviewResult === 'wrong' ? REVIEW_RETRY_AFTER_REPEAT_WRONG_MS : REVIEW_RETRY_AFTER_WRONG_MS;
  return {
    ...favorite,
    reviewCount: favorite.reviewCount + 1,
    lastReviewedAt: new Date(timestampMs).toISOString(),
    reviewStage: 1,
    nextReviewAt: new Date(timestampMs + retryDelayMs).toISOString(),
    lastReviewResult: 'wrong',
    consecutiveCorrect: 0,
    wrongCount: favorite.wrongCount + 1,
    isMastered: false,
    masteredAt: null,
  };
};

const isFavoriteDue = (favorite: FavoriteItem, referenceMs = Date.now()) => {
  if (favorite.isMastered) return false;
  if (!favorite.nextReviewAt) return false;
  const dueAt = Date.parse(favorite.nextReviewAt);
  return Number.isFinite(dueAt) && dueAt <= referenceMs;
};

const sortFavoritesForReview = (favorites: FavoriteItem[]) =>
  [...favorites].sort((a, b) => {
    const aDue = a.nextReviewAt ? Date.parse(a.nextReviewAt) : Number.MAX_SAFE_INTEGER;
    const bDue = b.nextReviewAt ? Date.parse(b.nextReviewAt) : Number.MAX_SAFE_INTEGER;
    if (aDue !== bDue) return aDue - bDue;
    return Date.parse(b.addedAt) - Date.parse(a.addedAt);
  });

const buildPracticeItems = (errors: GrammarError[], favorites: FavoriteItem[]): PracticeItem[] => {
  const favoriteByErrorId = new Map(favorites.map((favorite) => [favorite.error.id, favorite]));
  const groupedBySentence = new Map<string, GrammarError[]>();

  errors.forEach((error) => {
    const key = sentenceKey(error.originalSentence);
    const group = groupedBySentence.get(key) || [];
    group.push(error);
    groupedBySentence.set(key, group);
  });

  return Array.from(groupedBySentence.values()).flatMap((group) =>
    group.map((targetError) => {
      const siblingCount = Math.max(0, group.length - 1);
      const practiceNote =
        siblingCount > 0
          ? `本轮只需要改正“${ERROR_CATEGORY_LABELS[targetError.category]}”这个问题。同一句里还有 ${siblingCount} 个其他错误，暂时不用一次全改完。`
          : '';
      const acceptableAnswers = Array.from(
        new Set(
          [targetError.practiceSentence, targetError.correctedSentence]
            .map((text) => text?.trim() || '')
            .filter(Boolean)
        )
      );

      return {
        error: {
          ...targetError,
          explanationZh: [practiceNote, targetError.explanationZh || ''].filter(Boolean).join(' '),
        },
        categories: [targetError.category],
        acceptableAnswers:
          acceptableAnswers.length > 0 ? acceptableAnswers : [targetError.correctedSentence],
        sourceErrors: group,
        favorite: favoriteByErrorId.get(targetError.id) || null,
        userAttempt: '',
        isCorrect: null,
        attempts: 0,
      };
    })
  );
};

interface AppState {
  // Essays
  essays: Essay[];
  currentEssay: Essay | null;
  addEssay: (title: string, content: string) => void;
  addEssayWithAI: (title: string, content: string) => Promise<void>;
  isAnalyzing: boolean;
  analysisError: string | null;
  setCurrentEssay: (essay: Essay | null) => void;
  loadUserEssays: () => Promise<void>;
  isLoadingEssays: boolean;
  deleteEssay: (essayId: string) => Promise<void>;
  isDeletingEssay: string | null;
  updateErrorContent: (
    essayId: string,
    errorId: string,
    updates: Partial<Pick<GrammarError, 'correctedSentence' | 'explanationZh'>>
  ) => Promise<void>;

  // Practice
  practiceItems: PracticeItem[];
  currentPracticeIndex: number;
  startPractice: (errors: GrammarError[]) => void;
  startDuePractice: () => void;
  submitAnswer: (answer: string) => Promise<void>;
  nextPractice: () => void;

  // Favorites / Collection
  favorites: FavoriteItem[];
  addToFavorites: (error: GrammarError) => Promise<void>;
  removeFromFavorites: (errorId: string) => Promise<void>;
  loadFavorites: () => Promise<void>;

  // Navigation state
  activeTab: 'home' | 'upload' | 'analysis' | 'practice' | 'collection' | 'admin' | 'auth';
  setActiveTab: (tab: 'home' | 'upload' | 'analysis' | 'practice' | 'collection' | 'admin' | 'auth') => void;

  // Analysis filter
  categoryFilter: ErrorCategory | 'all';
  setCategoryFilter: (cat: ErrorCategory | 'all') => void;

  // User / Auth
  user: AppUser | null;
  setUser: (user: AppUser | null) => void;
  logout: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      essays: [],
      currentEssay: null,
      activeTab: 'home',
      categoryFilter: 'all',
      isAnalyzing: false,
      analysisError: null,
      isLoadingEssays: false,
      isDeletingEssay: null,
      user: null,

      // Legacy mock method (kept for fallback)
      addEssay: (title, content) => {
        const mockErrors: GrammarError[] = [
          {
            id: `err-new-${Date.now()}-1`,
            originalSentence: content.split('.')[0] + '.',
            correctedSentence: content.split('.')[0]?.replace(/\b(\w+)s (\w+s)\b/, '$1 $2s') + '.',
            practiceSentence: content.split('.')[0]?.replace(/\b(\w+)s (\w+s)\b/, '$1 $2s') + '.',
            category: 'subject-verb-agreement',
            explanationEn: 'This is a simulated grammar detection. In the full product, AI will provide precise explanations.',
            explanationZh: '这是一个模拟语法检测示例。正式分析时，AI 会提供更准确的解释。',
            essayTitle: title,
            createdAt: new Date().toISOString(),
          },
        ];
        const newEssay: Essay = {
          id: `essay-${Date.now()}`,
          title,
          content,
          uploadedAt: new Date().toISOString(),
          errors: mockErrors,
          analyzed: true,
        };
        set(state => ({
          essays: [newEssay, ...state.essays],
          currentEssay: newEssay,
          activeTab: 'analysis',
        }));
      },

      // Real AI-powered analysis via the server-side model API
      addEssayWithAI: async (title, content) => {
        set({ isAnalyzing: true, analysisError: null });
        try {
          const errors = await analyzeEssay(title, content);
          const normalizedTitle = title || DEFAULT_ESSAY_TITLE;
          let newEssay: Essay = {
            id: `essay-${Date.now()}`,
            title: normalizedTitle,
            content,
            uploadedAt: new Date().toISOString(),
            errors: errors as GrammarError[],
            analyzed: true,
          };

          // If user is logged in, persist first so the current essay has stable backend ids.
          const { user } = get();
          if (user) {
            try {
              const saveRes = await saveEssayToBackend(normalizedTitle, content, errors as GrammarError[]);
              newEssay = saveRes.data;
              console.log('[Store] Essay saved to backend for user:', user.id, 'dbId:', newEssay.dbId);
            } catch (err) {
              console.error('[Store] Failed to save essay to backend:', err);
            }
          }

          set(state => ({
            essays: [newEssay, ...state.essays],
            currentEssay: newEssay,
            activeTab: 'analysis',
            isAnalyzing: false,
          }));
        } catch (error: any) {
          console.error('AI analysis failed:', error);
          set({ isAnalyzing: false, analysisError: error.message || '分析失败' });
        }
      },

      setCurrentEssay: (essay) => set({ currentEssay: essay }),

      updateErrorContent: async (essayId, errorId, updates) => {
        const normalizedUpdates = {
          correctedSentence: updates.correctedSentence?.trim() ?? '',
          explanationZh: updates.explanationZh?.trim() ?? '',
        };

        const applyStateUpdate = (payload: Partial<Pick<GrammarError, 'correctedSentence' | 'explanationZh'>>) => {
          const applyErrorUpdate = (error: GrammarError): GrammarError => {
            if (error.id !== errorId) return error;
            return {
              ...error,
              ...payload,
            };
          };

          set((state) => {
            const nextEssays = state.essays.map((essay) =>
              essay.id === essayId
                ? { ...essay, errors: essay.errors.map(applyErrorUpdate) }
                : essay
            );
            const nextCurrentEssay =
              state.currentEssay?.id === essayId
                ? {
                    ...state.currentEssay,
                    errors: state.currentEssay.errors.map(applyErrorUpdate),
                  }
                : state.currentEssay;
            const nextFavorites = state.favorites.map((fav) =>
              fav.error.id === errorId ? { ...fav, error: applyErrorUpdate(fav.error) } : fav
            );
            const nextPracticeItems = state.practiceItems.map((item) => {
              const sourceMatches = item.sourceErrors.some((err) => err.id === errorId);
              if (!sourceMatches) return item;
              const updatedSourceErrors = item.sourceErrors.map(applyErrorUpdate);
              const updatedPrimaryError =
                item.error.id === errorId ? applyErrorUpdate(item.error) : item.error;
              const acceptableAnswers = Array.from(
                new Set(
                  [updatedPrimaryError.practiceSentence, updatedPrimaryError.correctedSentence]
                    .map((text) => text?.trim() || '')
                    .filter(Boolean)
                )
              );
              return {
                ...item,
                error: updatedPrimaryError,
                sourceErrors: updatedSourceErrors,
                acceptableAnswers: acceptableAnswers.length > 0 ? acceptableAnswers : item.acceptableAnswers,
              };
            });

            return {
              essays: nextEssays,
              currentEssay: nextCurrentEssay,
              favorites: nextFavorites,
              practiceItems: nextPracticeItems,
            };
          });
        };

        const { essays, currentEssay } = get();
        const essay =
          essays.find((item) => item.id === essayId) ??
          (currentEssay?.id === essayId ? currentEssay : null);
        const backendEssayId = parseBackendEssayId(essay, essayId);
        const backendErrorId = parseBackendErrorId(errorId);

        if (backendEssayId && backendErrorId) {
          const response = await updateEssayErrorInBackend(backendEssayId, backendErrorId, {
            correctedSentence: normalizedUpdates.correctedSentence,
            explanationZh: normalizedUpdates.explanationZh,
          });
          applyStateUpdate({
            correctedSentence: response.data.correctedSentence,
            explanationZh: response.data.explanationZh,
          });
          return;
        }

        applyStateUpdate(normalizedUpdates);
      },

      // Delete essay
      deleteEssay: async (essayId) => {
        set({ isDeletingEssay: essayId });
        try {
          // If user is logged in, delete from backend
          const { user, essays, currentEssay } = get();
          if (user) {
            const essay = essays.find(e => e.id === essayId);
            if (essay) {
              try {
                const backendId =
                  essay.dbId ??
                  (essay.id.startsWith('db-') ? Number(essay.id.slice(3)) : null);

                if (backendId && Number.isFinite(backendId)) {
                  await deleteEssayFromBackend(backendId);
                  console.log('[Store] Deleted essay from backend:', backendId);
                } else {
                  console.warn('[Store] Essay has no backend id, deleting locally only:', essayId);
                }
              } catch (err) {
                console.error('[Store] Failed to delete from backend (removing locally):', err);
              }
            }
          }
          // Remove from local state
          const essayToDelete = get().essays.find(e => e.id === essayId) ?? null;
          const removedErrorIds = new Set((essayToDelete?.errors || []).map((error) => error.id));
          const updatedEssays = get().essays.filter(e => e.id !== essayId);
          set({
            essays: updatedEssays,
            currentEssay: currentEssay?.id === essayId ? (updatedEssays[0] || null) : currentEssay,
            favorites: get().favorites.filter((fav) => !removedErrorIds.has(fav.error.id)),
            isDeletingEssay: null,
          });
        } catch (error) {
          console.error('[Store] Delete essay failed:', error);
          set({ isDeletingEssay: null });
        }
      },

      // Load essays from backend for logged-in user
      loadUserEssays: async () => {
        const { user } = get();
        if (!user) return;
        
        set({ isLoadingEssays: true, essays: [], currentEssay: null });
        try {
          const backendEssays = await fetchUserEssays();
          console.log('[Store] Loaded', backendEssays.length, 'essays from backend');
          set({ essays: backendEssays, isLoadingEssays: false });
        } catch (err) {
          console.error('[Store] Failed to load essays:', err);
          set({ isLoadingEssays: false });
        }
      },

      loadFavorites: async () => {
        const { user } = get();
        if (!user) {
          set({ favorites: [] });
          return;
        }

        try {
          const backendFavorites = await fetchFavoritesFromBackend();
          set({ favorites: backendFavorites });
        } catch (err) {
          console.error('[Store] Failed to load favorites:', err);
        }
      },

      practiceItems: [],
      currentPracticeIndex: 0,

      startPractice: (errors) => {
        const items = buildPracticeItems(errors, get().favorites);
        set({ practiceItems: items, currentPracticeIndex: 0, activeTab: 'practice' });
      },

      startDuePractice: () => {
        const dueErrors = sortFavoritesForReview(get().favorites)
          .filter((favorite) => isFavoriteDue(favorite))
          .map((favorite) => favorite.error);
        const items = buildPracticeItems(dueErrors, get().favorites);
        set({ practiceItems: items, currentPracticeIndex: 0, activeTab: 'practice' });
      },

      submitAnswer: async (answer) => {
        const { practiceItems, currentPracticeIndex } = get();
        const current = practiceItems[currentPracticeIndex];
        if (!current) return;

        const normalizedAnswer = normalizeForCompare(answer);
        const candidates = (current.acceptableAnswers?.length ? current.acceptableAnswers : [current.error.correctedSentence])
          .map(normalizeForCompare);
        const isCorrect = candidates.includes(normalizedAnswer);

        const updated = [...practiceItems];
        updated[currentPracticeIndex] = {
          ...current,
          userAttempt: answer,
          isCorrect,
          attempts: current.attempts + 1,
        };

        set({ practiceItems: updated });

        const mergeFavoriteIntoState = (favorite: FavoriteItem) => {
          set((state) => {
            const existingIndex = state.favorites.findIndex((item) => item.error.id === favorite.error.id);
            const nextFavorites =
              existingIndex === -1
                ? [favorite, ...state.favorites]
                : state.favorites.map((item, index) => (index === existingIndex ? favorite : item));
            const nextPracticeItems = state.practiceItems.map((item, index) => {
              if (index !== currentPracticeIndex) return item;
              const sourceMatches = item.sourceErrors.some((error) => error.id === favorite.error.id);
              if (!sourceMatches) return item;
              return { ...item, favorite };
            });

            return {
              favorites: nextFavorites,
              practiceItems: nextPracticeItems,
            };
          });
        };

        const sourceErrors = [current.error];
        const { favorites, user } = get();

        if (isCorrect) {
          sourceErrors.forEach((error) => {
            const backendErrorId = parseBackendErrorId(error.id);
            const existingFavorite = favorites.find((fav) => fav.error.id === error.id);

            if (user && backendErrorId) {
              const request = existingFavorite
                ? reviewFavoriteInBackend(backendErrorId, 'correct')
                : addFavoriteToBackend(backendErrorId, 'correct');
              void request
                .then((response) => {
                  mergeFavoriteIntoState(response.data);
                })
                .catch((err) => {
                  console.error('[Store] Failed to update review progress:', err);
                });
              return;
            }

            const localFavorite = existingFavorite
              ? applyLocalReviewResult(existingFavorite, 'correct')
              : buildInitialFavoriteState(error, 'correct');
            mergeFavoriteIntoState(localFavorite);
          });
          return;
        }

        sourceErrors.forEach((error) => {
          const existingFavorite = favorites.find((fav) => fav.error.id === error.id);
          if (!existingFavorite) return;

          const backendErrorId = parseBackendErrorId(error.id);
          if (user && backendErrorId) {
            void reviewFavoriteInBackend(backendErrorId, 'wrong')
              .then((response) => {
                mergeFavoriteIntoState(response.data);
              })
              .catch((err) => {
                console.error('[Store] Failed to update failed review:', err);
              });
            return;
          }

          mergeFavoriteIntoState(applyLocalReviewResult(existingFavorite, 'wrong'));
        });
      },

      nextPractice: () => {
        set(state => ({
          currentPracticeIndex: Math.min(state.currentPracticeIndex + 1, state.practiceItems.length - 1),
        }));
      },

      favorites: [],

      addToFavorites: async (error) => {
        const { favorites } = get();
        if (favorites.some(f => f.error.id === error.id)) return;

        const backendErrorId = parseBackendErrorId(error.id);
        const { user } = get();
        if (user && backendErrorId) {
          try {
            const response = await addFavoriteToBackend(backendErrorId);
            const nextFavorites = get().favorites;
            if (nextFavorites.some((fav) => fav.error.id === response.data.error.id)) return;
            set({ favorites: [response.data, ...nextFavorites] });
            return;
          } catch (err) {
            console.error('[Store] Failed to add favorite to backend:', err);
          }
        }

        set({
          favorites: [...favorites, buildInitialFavoriteState(error)],
        });
      },

      removeFromFavorites: async (errorId) => {
        const backendErrorId = parseBackendErrorId(errorId);
        const { user } = get();
        if (user && backendErrorId) {
          try {
            await removeFavoriteFromBackend(backendErrorId);
          } catch (err) {
            console.error('[Store] Failed to remove favorite from backend:', err);
          }
        }

        set(state => ({
          favorites: state.favorites.filter(f => f.error.id !== errorId),
        }));
      },

      setActiveTab: (tab) => set({ activeTab: tab }),
      setCategoryFilter: (cat) => set({ categoryFilter: cat }),

      setUser: (user) =>
        set(() =>
          user
            ? {
                user,
                essays: [],
                currentEssay: null,
                favorites: [],
                practiceItems: [],
                currentPracticeIndex: 0,
                categoryFilter: 'all',
              }
            : {
                user: null,
                essays: [],
                currentEssay: null,
                favorites: [],
                practiceItems: [],
                currentPracticeIndex: 0,
                categoryFilter: 'all',
              }
        ),
      logout: () =>
        set({
          user: null,
          essays: [],
          currentEssay: null,
          favorites: [],
          practiceItems: [],
          currentPracticeIndex: 0,
          activeTab: 'home',
          categoryFilter: 'all',
        }),
    }),
    {
      name: 'refine-storage',
      partialize: (state) => ({
        user: state.user,
      }),
    }
  )
);
